// plugins/errorMessage.client.ts
import { defineNuxtPlugin } from "#app";
import { useUiStore } from "~/stores/ui/uistore";
import type { AxiosError } from "axios";
import type { StoreApiError } from "~/model/api/pLCnextStoreFrontendAPI.schemas";

interface ErrorResponse {
  error: string;
  message: string;
}

export default defineNuxtPlugin(() => {
  console.debug("errorMessage.client.ts loaded");
  return {
    provide: {
      errorMessage: (err: AxiosError<unknown> | unknown): void => {
        const uiStore = useUiStore();
        const { message, error } = getErrorAndMessage(err);

        console.warn("Received error message", error, message);

        uiStore.setMessage({
          title: "An error occurred",
          message,
          type: "error",
          contactSupport: getContactSupport(err),
          error,
        });
      },
    },
  };
});

function getContactSupport(err: AxiosError<unknown> | unknown): boolean {
  if (isAxiosError(err)) {
    const status = err.response?.status;
    if (status) {
      return status >= 500 || status == 400;
    }
  }
  return false;
}

export function getErrorAndMessage(
  errorData: AxiosError<unknown> | unknown,
): ErrorResponse {
  // Type guard for logged out response
  if (
    isAxiosError(errorData) &&
    (isLoggedOutError(errorData.response) ||
      isLoggedOutError(errorData.response?.data))
  ) {
    return {
      error: String(errorData),
      message: "You have been automatically logged out, please log in again.",
    };
  }

  // Type guard for AxiosError with StoreApiError response
  if (isAxiosError(errorData) && isStoreApiError(errorData.response?.data)) {
    const storeApiError = errorData.response.data;
    let message: string | undefined;
    if (storeApiError.errors?.length === 1) {
      message = storeApiError.errors[0];
    } else if (
      storeApiError.errors?.length &&
      storeApiError.errors?.length > 1
    ) {
      message = storeApiError.errors?.join(",");
    } else {
      message = undefined;
    }

    return {
      error: `${storeApiError.status} (errorId: ${storeApiError.errorId})`,
      message: message || "An unknown error occurred.",
    };
  }

  // client validation errors
  if (isClientValidationError(errorData)) {
    return {
      error: "Validation Error",
      message: errorData.message,
    };
  }

  // Fallback for unknown errors
  return {
    error: String(errorData),
    message: "An unknown error occurred.",
  };
}

function isClientValidationError(error: unknown): error is Error {
  return error instanceof Error && error.name === "ClientValidationError";
}

export function isAxiosError(error: unknown): error is AxiosError<unknown> {
  return (
    typeof error === "object" &&
    error !== null &&
    "response" in error &&
    "config" in error &&
    "isAxiosError" in error
  );
}

export function isStoreApiError(data: unknown): data is StoreApiError {
  return (
    typeof data === "object" &&
    data !== null &&
    "errors" in data &&
    Array.isArray((data as Partial<StoreApiError>).errors)
  );
}

function isLoggedOutError(data: unknown): data is Record<string, boolean> {
  return (
    typeof data === "object" &&
    data !== null &&
    "loggedOut" in data &&
    (data as Record<string, unknown>).loggedOut === true
  );
}
